/**
 * CreativeDnaScreen — Phase 2 of Rammy Studio.
 *
 * Lets the user shape Rammy's writing voice by editing 4 DNA slices per
 * project: Voice (tone/formality/humour), Strategic (audience, positioning,
 * approved/prohibited claims), Negative preferences (bans), Evidence
 * (product facts, testimonials, disclaimers).
 *
 * Multi-project support: personal vs company vs client. One project can be
 * marked default — that's the one the chat pipeline auto-injects into the
 * system prompt when the user asks Rammy to write something.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import * as L from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

// Vocab we surface as constrained pickers — keeps user & LLM aligned.
const OPT = {
  formality: ["casual", "professional", "formal"],
  sentence_length: ["short", "mixed", "long"],
  vocabulary: ["simple", "advanced", "mixed"],
  humour: ["none", "dry", "playful", "sharp"],
  assertiveness: ["reserved", "confident", "bold"],
  storytelling: ["rare", "regular", "primary"],
  opening_style: ["hook question", "context first", "direct"],
  cta_style: ["soft invite", "direct ask", "none"],
  objective: ["awareness", "trust", "conversion", "community"],
  awareness_stage: ["unaware", "problem", "solution", "product", "most"],
  kind: ["personal", "company", "side_project", "client", "other"],
};

export function CreativeDnaScreen({ go }) {
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const active = useMemo(
    () => projects.find((p) => p.id === activeId) || null,
    [projects, activeId]
  );

  // Initial load — projects + auto-seed default if none exist.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: list } = await api.get("/dna/projects");
        if (!list || list.length === 0) {
          const { data: def } = await api.get("/dna/default");
          setProjects([def]);
          setActiveId(def.id);
        } else {
          setProjects(list);
          const def = list.find((p) => p.is_default) || list[0];
          setActiveId(def.id);
        }
      } catch (e) {
        toast.error("Couldn't load Creative DNA");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Mutate a slice locally, mark dirty. Real save happens on the button.
  const updateSlice = (slice, patch) => {
    setProjects((ps) => ps.map((p) => {
      if (p.id !== activeId) return p;
      return { ...p, [slice]: { ...(p[slice] || {}), ...patch } };
    }));
    setDirty(true);
  };
  const updateMeta = (patch) => {
    setProjects((ps) => ps.map((p) => (p.id === activeId ? { ...p, ...patch } : p)));
    setDirty(true);
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const { data: updated } = await api.patch(`/dna/projects/${active.id}`, {
        name: active.name, kind: active.kind, is_default: active.is_default,
        voice: active.voice, strategic: active.strategic,
        negative: active.negative, evidence: active.evidence,
      });
      setProjects((ps) => ps.map((p) => (p.id === updated.id ? updated : p)));
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const newProject = async () => {
    const name = window.prompt("New project name (e.g. 'Company', 'Side Hustle')");
    if (!name) return;
    try {
      const { data: created } = await api.post("/dna/projects", {
        name: name.trim(), kind: "personal", is_default: projects.length === 0,
      });
      setProjects((ps) => [...ps, created]);
      setActiveId(created.id);
      toast.success("Project created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create project");
    }
  };

  const removeProject = async () => {
    if (!active) return;
    if (!window.confirm(`Delete project "${active.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/dna/projects/${active.id}`);
      const remaining = projects.filter((p) => p.id !== active.id);
      setProjects(remaining);
      setActiveId(remaining[0]?.id || null);
      toast.success("Project deleted");
    } catch (e) {
      toast.error("Couldn't delete");
    }
  };

  if (loading) {
    return (
      <div className="p-5 flex items-center justify-center text-[var(--rammy-text-muted)]" data-testid="screen-dna-loading">
        <L.Loader2 className="animate-spin" size={16}/>
        <span className="ml-2 text-sm">Loading your Creative DNA…</span>
      </div>
    );
  }

  return (
    <div className="p-5 space-y-4 pb-24" data-testid="screen-creative-dna">
      {/* Header + Back */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => go("settings")}
          className="p-2 rounded-full hover:bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-muted)]"
          data-testid="dna-back"
          aria-label="Back to Settings"
        >
          <L.ArrowLeft size={16}/>
        </button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)]">
            Rammy Studio · Phase 2
          </div>
          <h1 className="text-xl font-display text-[var(--rammy-text)]">Your Creative DNA</h1>
          <div className="text-[12px] text-[var(--rammy-text-sec)] mt-0.5">
            Shape how Rammy writes for you. This gets baked into every LinkedIn
            post, blog draft, email, and legal notice Rammy produces.
          </div>
        </div>
      </div>

      {/* Project switcher */}
      <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4">
        <div className="flex items-center gap-2 flex-wrap">
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => { if (dirty && !window.confirm("Discard unsaved changes?")) return; setActiveId(p.id); setDirty(false); }}
              data-testid={`dna-project-${p.id}`}
              className={`h-8 px-3 rounded-full text-[12px] font-medium transition inline-flex items-center gap-1.5 ${
                p.id === activeId
                  ? "bg-[var(--rm-ink-900)] text-white"
                  : "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text)] hover:bg-[var(--rammy-cyan)]/15"
              }`}
            >
              {p.name}
              {p.is_default && <L.Star size={11} className="fill-current"/>}
            </button>
          ))}
          <button
            onClick={newProject}
            data-testid="dna-new-project"
            className="h-8 px-3 rounded-full text-[12px] font-medium bg-[var(--rm-surface)] border border-dashed border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] text-[var(--rammy-text-sec)] inline-flex items-center gap-1"
          >
            <L.Plus size={12}/> New project
          </button>
        </div>
      </div>

      {active && (
        <>
          {/* Project meta */}
          <DnaCard title="Project" hint="Name and type. One project can be your default.">
            <div className="grid grid-cols-2 gap-3">
              <LabeledField label="Name">
                <Input
                  value={active.name}
                  onChange={(e) => updateMeta({ name: e.target.value })}
                  className="h-9 rounded-xl"
                  data-testid="dna-meta-name"
                />
              </LabeledField>
              <LabeledField label="Kind">
                <PickerSelect value={active.kind} options={OPT.kind} onChange={(v) => updateMeta({ kind: v })}/>
              </LabeledField>
              <label className="col-span-2 flex items-center justify-between rounded-xl bg-[var(--rammy-surface-alt)] px-3 py-2">
                <span className="text-[13px]">Use as default for chat writing</span>
                <Switch
                  checked={!!active.is_default}
                  onCheckedChange={(v) => updateMeta({ is_default: v })}
                  data-testid="dna-meta-default"
                />
              </label>
            </div>
          </DnaCard>

          {/* Voice */}
          <DnaCard title="Voice" hint="How you sound. Rammy will mirror these across posts and emails.">
            <div className="grid grid-cols-2 gap-3">
              <LabeledField label="Tone (free text)">
                <Input value={active.voice?.tone || ""} onChange={(e) => updateSlice("voice", { tone: e.target.value })} placeholder="warm, understated" className="h-9 rounded-xl" data-testid="dna-voice-tone"/>
              </LabeledField>
              <SelectField label="Formality" value={active.voice?.formality} options={OPT.formality} onChange={(v) => updateSlice("voice", { formality: v })}/>
              <SelectField label="Sentence length" value={active.voice?.sentence_length} options={OPT.sentence_length} onChange={(v) => updateSlice("voice", { sentence_length: v })}/>
              <SelectField label="Vocabulary" value={active.voice?.vocabulary} options={OPT.vocabulary} onChange={(v) => updateSlice("voice", { vocabulary: v })}/>
              <SelectField label="Humour" value={active.voice?.humour} options={OPT.humour} onChange={(v) => updateSlice("voice", { humour: v })}/>
              <SelectField label="Assertiveness" value={active.voice?.assertiveness} options={OPT.assertiveness} onChange={(v) => updateSlice("voice", { assertiveness: v })}/>
              <SelectField label="Storytelling" value={active.voice?.storytelling} options={OPT.storytelling} onChange={(v) => updateSlice("voice", { storytelling: v })}/>
              <SelectField label="Opening style" value={active.voice?.opening_style} options={OPT.opening_style} onChange={(v) => updateSlice("voice", { opening_style: v })}/>
              <SelectField label="CTA style" value={active.voice?.cta_style} options={OPT.cta_style} onChange={(v) => updateSlice("voice", { cta_style: v })}/>
            </div>
            <ListEditor
              label="Signature phrases you actually use"
              hint="Words or short phrases Rammy can lean on to sound like you."
              value={active.voice?.signature_phrases || []}
              onChange={(v) => updateSlice("voice", { signature_phrases: v })}
              placeholder="e.g. quietly ambitious"
              testId="dna-voice-phrases"
            />
          </DnaCard>

          {/* Strategic */}
          <DnaCard title="Strategic" hint="Who you're talking to and what you want them to think, feel, or do.">
            <div className="grid grid-cols-2 gap-3">
              <LabeledField label="Audience">
                <Textarea value={active.strategic?.audience || ""} onChange={(e) => updateSlice("strategic", { audience: e.target.value })} placeholder="e.g. Series A founders shipping AI products" className="rounded-xl min-h-[64px]" data-testid="dna-strategic-audience"/>
              </LabeledField>
              <LabeledField label="Positioning">
                <Textarea value={active.strategic?.positioning || ""} onChange={(e) => updateSlice("strategic", { positioning: e.target.value })} placeholder="e.g. Warm, product-first, not consulting-jargon" className="rounded-xl min-h-[64px]" data-testid="dna-strategic-positioning"/>
              </LabeledField>
              <SelectField label="Primary objective" value={active.strategic?.objective} options={OPT.objective} onChange={(v) => updateSlice("strategic", { objective: v })}/>
              <SelectField label="Audience awareness stage" value={active.strategic?.awareness_stage} options={OPT.awareness_stage} onChange={(v) => updateSlice("strategic", { awareness_stage: v })}/>
            </div>
            <ListEditor label="Approved claims (Rammy can cite these)" value={active.strategic?.approved_claims || []} onChange={(v) => updateSlice("strategic", { approved_claims: v })} placeholder="e.g. 98% on-time delivery across 2024" testId="dna-strategic-approved"/>
            <ListEditor label="Prohibited claims (never say these)" value={active.strategic?.prohibited_claims || []} onChange={(v) => updateSlice("strategic", { prohibited_claims: v })} placeholder="e.g. 'guaranteed' anything" testId="dna-strategic-prohibited"/>
            <ListEditor label="Differentiators" value={active.strategic?.differentiators || []} onChange={(v) => updateSlice("strategic", { differentiators: v })} placeholder="e.g. Same-day pickup across 12 cities" testId="dna-strategic-diffs"/>
            <ListEditor label="Common objections to address" value={active.strategic?.objections || []} onChange={(v) => updateSlice("strategic", { objections: v })} placeholder="e.g. 'AI can't handle nuance'" testId="dna-strategic-objections"/>
          </DnaCard>

          {/* Negative preferences */}
          <DnaCard title="Never do this" hint="Phrases, tones, and structures Rammy should avoid.">
            <ListEditor label="Banned phrases" value={active.negative?.banned_phrases || []} onChange={(v) => updateSlice("negative", { banned_phrases: v })} placeholder="e.g. 'game-changer', 'buckle up'" testId="dna-negative-phrases"/>
            <ListEditor label="Banned tones" value={active.negative?.banned_tones || []} onChange={(v) => updateSlice("negative", { banned_tones: v })} placeholder="e.g. corporate, salesy" testId="dna-negative-tones"/>
            <ListEditor label="Structures to avoid" value={active.negative?.banned_structures || []} onChange={(v) => updateSlice("negative", { banned_structures: v })} placeholder="e.g. 'three-part rhetorical closer'" testId="dna-negative-structures"/>
            <ListEditor label="Formatting dislikes" value={active.negative?.formatting_dislikes || []} onChange={(v) => updateSlice("negative", { formatting_dislikes: v })} placeholder="e.g. 'emoji bullet lists'" testId="dna-negative-format"/>
          </DnaCard>

          {/* Evidence */}
          <DnaCard title="Evidence" hint="Real material Rammy can pull from — nothing invented.">
            <ListEditor label="Product facts (verified, quotable)" value={active.evidence?.product_facts || []} onChange={(v) => updateSlice("evidence", { product_facts: v })} placeholder="e.g. Ships in 24h across the EU" testId="dna-evidence-facts"/>
            <ListEditor label="Approved testimonials" value={active.evidence?.testimonials || []} onChange={(v) => updateSlice("evidence", { testimonials: v })} placeholder='e.g. "Cut our onboarding 4 days → 90 min." — Priya, Head of Ops @ Northline' testId="dna-evidence-testimonials"/>
            <ListEditor label="Required legal disclaimers" value={active.evidence?.legal_disclaimers || []} onChange={(v) => updateSlice("evidence", { legal_disclaimers: v })} placeholder="e.g. 'Past performance is not indicative of future results.'" testId="dna-evidence-disclaimers"/>
            <ListEditor label="Approved examples (paste your best posts)" value={active.evidence?.approved_examples || []} onChange={(v) => updateSlice("evidence", { approved_examples: v })} placeholder="Paste a post or email Rammy should learn your voice from" testId="dna-evidence-examples"/>
          </DnaCard>

          {/* Danger zone */}
          <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">Danger zone</div>
            <button
              onClick={removeProject}
              className="mt-2 text-[13px] text-rose-700 hover:text-rose-900 inline-flex items-center gap-1"
              data-testid="dna-delete-project"
            >
              <L.Trash2 size={13}/> Delete this project
            </button>
          </div>

          {/* Save bar */}
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30">
            <div className="flex items-center gap-3 rounded-full bg-[var(--rm-ink-900)] text-white shadow-[0_20px_50px_-12px_rgba(17,19,24,0.5)] px-3 py-2">
              <Badge className={`bg-transparent ${dirty ? "text-amber-300" : "text-emerald-300"} border-0`}>
                {dirty ? "Unsaved changes" : "All saved"}
              </Badge>
              <Button
                onClick={save}
                disabled={!dirty || saving}
                data-testid="dna-save"
                className="h-8 px-4 rounded-full text-[12px] font-semibold bg-[var(--rm-surface)] text-[var(--rammy-text)] hover:bg-[var(--rammy-cyan)] hover:text-white"
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Reusable UI primitives ─────────────────────────────────────────────
function DnaCard({ title, hint, children }) {
  return (
    <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4 space-y-3">
      <div>
        <div className="text-[13px] font-semibold text-[var(--rammy-text)]">{title}</div>
        {hint && <div className="text-[11px] text-[var(--rammy-text-muted)] mt-0.5">{hint}</div>}
      </div>
      {children}
    </div>
  );
}
function LabeledField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] mb-1">{label}</div>
      {children}
    </label>
  );
}
function SelectField({ label, value, options, onChange }) {
  return (
    <LabeledField label={label}>
      <PickerSelect value={value} options={options} onChange={onChange}/>
    </LabeledField>
  );
}
function PickerSelect({ value, options, onChange }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-9 rounded-xl text-[13px]"><SelectValue placeholder="—"/></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o} className="text-[13px]">{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * ListEditor — simple chip-list editor. Enter or "+" to add, X to remove.
 * We keep the UX minimal on purpose — the user is filling a lot of these.
 */
function ListEditor({ label, hint, value, onChange, placeholder, testId }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (value.includes(v)) { setInput(""); return; }
    onChange([...(value || []), v]);
    setInput("");
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">{label}</div>
      {hint && <div className="text-[11px] text-[var(--rammy-text-muted)]/80 mt-0.5">{hint}</div>}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(value || []).map((chip, i) => (
          <span key={i} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-[var(--rammy-surface-alt)] text-[12px] text-[var(--rammy-text)]">
            <span className="max-w-[280px] truncate">{chip}</span>
            <button onClick={() => remove(i)} className="text-[var(--rammy-text-muted)] hover:text-rose-600" aria-label="Remove"><L.X size={11}/></button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="h-9 rounded-xl flex-1 text-[13px]"
          data-testid={testId ? `${testId}-input` : undefined}
        />
        <button
          onClick={add}
          className="h-9 px-3 rounded-full bg-[var(--rm-ink-900)] text-white text-[12px] font-semibold inline-flex items-center gap-1"
          data-testid={testId ? `${testId}-add` : undefined}
        >
          <L.Plus size={12}/> Add
        </button>
      </div>
    </div>
  );
}
